/**
 * Utilidades para obtener nombres correctos de superficies dentales
 * basados en la nomenclatura dental estándar
 */

/**
 * Obtiene el nombre correcto de una superficie dental basado en el diente y superficie
 * @param {string} toothLabel - Etiqueta del diente (ej: '1.1', '2.6')
 * @param {string} surfaceKey - Clave de superficie ('top', 'right', 'bottom', 'left', 'center')
 * @returns {string} - Nombre correcto de la superficie
 */
export const getSurfaceName = (toothLabel, surfaceKey) => {
  const toothNumber = toothLabel.replace('.', '');
  const quadrant = toothNumber.charAt(0);
  
  switch (surfaceKey) {
    case 'top': 
      // Cuadrantes superiores (1,2,5,6): top = Vestibular
      // Cuadrantes inferiores (3,4,7,8): top = Lingual
      if (quadrant === '1' || quadrant === '2' || quadrant === '5' || quadrant === '6') {
        return 'Vestibular';
      } else {
        return 'Lingual';
      }
      
    case 'right': 
      // Cuadrantes derechos (1,4,5,8): right = Mesial
      // Cuadrantes izquierdos (2,3,6,7): right = Distal
      if (quadrant === '1' || quadrant === '4' || quadrant === '5' || quadrant === '8') {
        return 'Mesial';
      } else {
        return 'Distal';
      }
      
    case 'bottom': 
      // Cuadrantes superiores (1,2,5,6): bottom = Palatina
      // Cuadrantes inferiores (3,4,7,8): bottom = Vestibular
      if (quadrant === '1' || quadrant === '2' || quadrant === '5' || quadrant === '6') {
        return 'Palatina';
      } else {
        return 'Vestibular';
      }
      
    case 'left': 
      // Cuadrantes derechos (1,4,5,8): left = Distal
      // Cuadrantes izquierdos (2,3,6,7): left = Mesial
      if (quadrant === '1' || quadrant === '4' || quadrant === '5' || quadrant === '8') {
        return 'Distal';
      } else {
        return 'Mesial';
      }
      
    case 'center': 
      // El centro siempre es oclusal
      return 'Oclusal';
      
    default: 
      return surfaceKey;
  }
};

/**
 * Obtiene un mapeo de todas las superficies para un diente específico
 * @param {string} toothLabel - Etiqueta del diente
 * @returns {Object} - Objeto con claves de superficie y nombres correctos
 */
export const getSurfaceNamesForTooth = (toothLabel) => {
  return {
    'top': getSurfaceName(toothLabel, 'top'),
    'right': getSurfaceName(toothLabel, 'right'), 
    'bottom': getSurfaceName(toothLabel, 'bottom'),
    'left': getSurfaceName(toothLabel, 'left'),
    'center': getSurfaceName(toothLabel, 'center')
  };
};

/**
 * Lista de todas las superficies en orden
 */
export const SURFACE_KEYS = ['top', 'right', 'bottom', 'left', 'center'];

/**
 * Returns true when the FDI label belongs to a primary (pediatric) tooth: quadrants 5-8.
 * @param {string} toothLabel - Tooth label (e.g. '5.1', '1.1')
 */
export const isPediatricTooth = (toothLabel) => {
  const quadrant = String(toothLabel).replace('.', '').charAt(0);
  return ['5', '6', '7', '8'].includes(quadrant);
};

/**
 * Picks which odontogram arch set to open for a set of treatment plan procedures.
 * Returns 'pediatric' when every selected tooth is primary, 'adult' when at least one
 * permanent tooth is selected, and null when no teeth are selected.
 * @param {Array<{selectedTeeth?: Object}>} procedures
 * @returns {'adult' | 'pediatric' | null}
 */
export const inferOdontogramMode = (procedures = []) => {
  const labels = procedures.flatMap(procedure => Object.keys(procedure?.selectedTeeth || {}));
  if (labels.length === 0) return null;
  return labels.every(isPediatricTooth) ? 'pediatric' : 'adult';
};
